import React, { useState, useEffect } from 'react';

const CacheUpdateManager = ({ showAsButton = false, className = '', adminMode = false }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [serviceWorkerStatus, setServiceWorkerStatus] = useState('unknown');
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [cacheSize, setCacheSize] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    checkServiceWorkerStatus();
    checkPlatform();
    
    // Listen for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
      
      // Check for existing service worker
      navigator.serviceWorker.ready.then(registration => {
        if (registration.waiting) {
          setUpdateAvailable(true);
        }
      });
    }
    
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, []);

  const checkPlatform = () => {
    const iosCheck = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const standaloneCheck = window.matchMedia('(display-mode: standalone)').matches || 
                           window.navigator.standalone === true;
    
    setIsIOS(iosCheck);
    setIsStandalone(standaloneCheck);
  };

  const checkServiceWorkerStatus = async () => {
    if (!('serviceWorker' in navigator)) {
      setServiceWorkerStatus('not-supported');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        setServiceWorkerStatus('not-registered');
        return;
      }

      if (registration.active) {
        setServiceWorkerStatus('active');
        
        // Try to estimate cache size (approximate)
        if ('storage' in navigator && 'estimate' in navigator.storage) {
          const estimate = await navigator.storage.estimate();
          setCacheSize(estimate.usage);
        }
      } else {
        setServiceWorkerStatus('installing');
      }
    } catch (error) {
      console.error('Error checking service worker status:', error);
      setServiceWorkerStatus('error');
    }
  };

  const handleServiceWorkerMessage = (event) => {
    const { data } = event;
    
    if (data.type === 'SW_UPDATED') {
      setUpdateAvailable(false);
      setLastUpdate(new Date());
    } else if (data.type === 'CACHE_REFRESHED') {
      setIsRefreshing(false);
      setLastUpdate(new Date());
    }
  };

  const handleCacheRefresh = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    
    try {
      // Use the global cache refresh function
      if (window.forceCacheRefresh) {
        await window.forceCacheRefresh();
        setLastUpdate(new Date());
        
        if (isIOS && isStandalone) {
          // For iOS home screen apps, suggest reloading after cache refresh
          const shouldReload = window.confirm(
            'Cache has been refreshed! To ensure all updates are applied, the app will reload. Continue?'
          );
          if (shouldReload) {
            window.location.reload();
          }
        }
      } else {
        throw new Error('Cache refresh function not available');
      }
    } catch (error) {
      console.error('Cache refresh failed:', error);
      alert('Failed to refresh cache. Please try reloading the page manually.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleUpdateApp = () => {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getPlatformInfo = () => {
    if (isIOS && isStandalone) {
      return {
        icon: '📱',
        text: 'iOS Home Screen App',
        color: '#4caf50',
        description: 'App is properly installed and can receive updates'
      };
    } else if (isIOS && !isStandalone) {
      return {
        icon: '⚠️',
        text: 'iOS Safari Browser',
        color: '#ff9800',
        description: 'Add to home screen for better update capabilities'
      };
    } else {
      return {
        icon: '💻',
        text: 'Desktop/Android',
        color: '#2196f3',
        description: 'Standard web app with automatic updates'
      };
    }
  };

  if (showAsButton) {
    return (
      <button
        onClick={handleCacheRefresh}
        disabled={isRefreshing || serviceWorkerStatus !== 'active'}
        className={`cache-refresh-btn ${className}`}
        style={{
          padding: '8px 16px',
          backgroundColor: isRefreshing ? '#ccc' : '#2196f3',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: isRefreshing ? 'not-allowed' : 'pointer',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        {isRefreshing ? (
          <>
            <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
            Refreshing...
          </>
        ) : (
          <>
            🔄 Refresh Cache
          </>
        )}
      </button>
    );
  }

  const platformInfo = getPlatformInfo();

  return (
    <div className={`cache-update-manager ${className}`} style={{
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '16px',
      margin: '16px 0',
      backgroundColor: '#f9f9f9'
    }}>
      <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px' }}>
        Cache & Update Manager
      </h3>
      
      {/* Platform Info */}
      <div style={{
        backgroundColor: platformInfo.color,
        color: 'white',
        padding: '8px 12px',
        borderRadius: '4px',
        marginBottom: '16px',
        fontSize: '14px'
      }}>
        <strong>{platformInfo.icon} {platformInfo.text}</strong>
        <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '4px' }}>
          {platformInfo.description}
        </div>
      </div>

      {/* Service Worker Status */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
          Service Worker Status:
        </div>
        <div style={{
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '13px',
          backgroundColor: serviceWorkerStatus === 'active' ? '#e8f5e8' : '#fff3cd',
          color: serviceWorkerStatus === 'active' ? '#2e7d2e' : '#856404',
          border: `1px solid ${serviceWorkerStatus === 'active' ? '#c3e6c3' : '#ffeaa7'}`
        }}>
          {serviceWorkerStatus === 'active' && '✅ Active and ready'}
          {serviceWorkerStatus === 'installing' && '⏳ Installing...'}
          {serviceWorkerStatus === 'not-supported' && '❌ Not supported'}
          {serviceWorkerStatus === 'not-registered' && '❌ Not registered'}
          {serviceWorkerStatus === 'error' && '❌ Error occurred'}
          {serviceWorkerStatus === 'unknown' && '⏳ Checking...'}
        </div>
      </div>

      {/* Cache Info */}
      {adminMode && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
            Cache Information:
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            <div>Size: {formatBytes(cacheSize)}</div>
            {lastUpdate && (
              <div>Last Updated: {lastUpdate.toLocaleString()}</div>
            )}
          </div>
        </div>
      )}

      {/* Update Available */}
      {updateAvailable && (
        <div style={{
          backgroundColor: '#e3f2fd',
          border: '1px solid #2196f3',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '16px'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '500', color: '#1976d2' }}>
            🎉 App Update Available!
          </div>
          <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
            A new version of the app is ready to install.
          </div>
          <button
            onClick={handleUpdateApp}
            style={{
              marginTop: '8px',
              padding: '6px 12px',
              backgroundColor: '#2196f3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            Update Now
          </button>
        </div>
      )}

      {/* iOS Guidance */}
      {isIOS && !isStandalone && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '16px'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '500', color: '#856404' }}>
            📱 For iPhone Users
          </div>
          <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
            Add this app to your home screen for better cache updates and offline functionality.
            Tap the share button in Safari and select "Add to Home Screen".
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <button
          onClick={handleCacheRefresh}
          disabled={isRefreshing || serviceWorkerStatus !== 'active'}
          style={{
            padding: '10px 16px',
            backgroundColor: isRefreshing ? '#ccc' : '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isRefreshing ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {isRefreshing ? (
            <>
              <span style={{ 
                display: 'inline-block',
                animation: 'spin 1s linear infinite',
                fontSize: '16px'
              }}>⏳</span>
              Refreshing Cache...
            </>
          ) : (
            <>
              🔄 Force Cache Refresh
            </>
          )}
        </button>

        {adminMode && (
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            🔄 Hard Reload
          </button>
        )}
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CacheUpdateManager; 